import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, BadgeCheck, Clock, AlertTriangle, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TONE = {
  verified: { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: BadgeCheck },
  review: { cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: Clock },
  illustrative: { cls: "text-orange-700 bg-orange-50 border-orange-200", Icon: AlertTriangle },
};

function GovBadge({ badge }) {
  const t = TONE[badge?.tone] || TONE.illustrative;
  const Icon = t.Icon;
  return (
    <span data-testid={`gov-badge-${badge?.status}`}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-semibold ${t.cls}`}>
      <Icon size={12} /> {badge?.label}
    </span>
  );
}

function SourceRow({ src, statuses, canVerify, onSet, onSaveContent }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(src.state?.note || "");
  const [content, setContent] = useState(src.state?.authoritative_content || "");
  const [editing, setEditing] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try { await onSet(src.id, status, note); }
    finally { setBusy(false); }
  };
  const saveContent = async () => {
    setSavingContent(true);
    try { await onSaveContent(src.id, content); setEditing(false); }
    finally { setSavingContent(false); }
  };
  const st = src.state || {};
  const hasContent = !!(st.authoritative_content || "").trim();
  return (
    <div data-testid={`gov-source-${src.id}`} className="border ek-line rounded-lg p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-[240px]">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold ek-text">{src.name}</span>
            <span className="text-[10px] ek-text-muted border ek-line rounded px-1.5 py-0.5">{src.engine}</span>
          </div>
          <div className="text-[11px] ek-text-muted mt-0.5">{src.detail}</div>
          <div className="text-[11px] ek-text-muted mt-1">{src.provenance}</div>
          {st.status === "verified" && st.verified_by && (
            <div className="text-[11px] text-emerald-700 mt-1">
              Verified by <b>{st.verified_by_name || st.verified_by}</b> · {(st.verified_at || "").slice(0, 10)}
              {st.version ? ` · v${st.version}` : ""}
            </div>
          )}
        </div>
        <GovBadge badge={src.badge} />
      </div>

      {/* Authoritative content — owner-provided, reviewable & approvable */}
      <div className="mt-3 border-t ek-line pt-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold ek-text-soft">
            <FileText size={12} /> Authoritative content
            {hasContent
              ? <span className="text-[10px] ek-text-muted font-normal">
                  {st.content_by_name ? `· ${st.content_by_name}` : ""} {st.content_at ? `· ${(st.content_at || "").slice(0, 10)}` : ""}
                </span>
              : <span className="text-[10px] text-orange-500 font-normal">· none provided yet</span>}
          </div>
          <button data-testid={`gov-edit-content-${src.id}`} onClick={() => setEditing((e) => !e)}
            className="text-[11px] text-[var(--e360-primary,#7737F6)] hover:underline">
            {editing ? "Cancel" : hasContent ? "Edit content" : "Add content"}
          </button>
        </div>
        {editing ? (
          <div className="mt-2">
            <textarea data-testid={`gov-content-${src.id}`} value={content} onChange={(e) => setContent(e.target.value)}
              rows={8} placeholder="Paste the authoritative methodology / provenance content (Markdown supported)…"
              className="w-full text-[12px] font-mono border ek-line rounded px-2 py-2 leading-relaxed" />
            <div className="mt-2 flex items-center gap-2">
              <button data-testid={`gov-save-content-${src.id}`} onClick={saveContent} disabled={savingContent}
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-[var(--e360-primary,#7737F6)] text-white disabled:opacity-60">
                {savingContent ? "Saving…" : "Save (→ under review)"}
              </button>
              <span className="text-[10.5px] ek-text-muted">Saving content sends it for owner verification.</span>
            </div>
          </div>
        ) : hasContent ? (
          <pre data-testid={`gov-content-preview-${src.id}`}
            className="mt-2 text-[11.5px] ek-text-soft whitespace-pre-wrap font-sans ek-surface-2 border ek-line rounded p-2 max-h-40 overflow-auto leading-relaxed">
            {st.authoritative_content}
          </pre>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input data-testid={`gov-note-${src.id}`} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Reviewer note (optional)"
          className="text-[12px] border ek-line rounded px-2 py-1 w-56" />
        {statuses.map((s) => {
          const ownerGated = (s === "verified") && !canVerify;
          const active = st.status === s;
          return (
            <button key={s} data-testid={`gov-set-${src.id}-${s}`} disabled={busy || active || ownerGated}
              onClick={() => set(s)} title={ownerGated ? "Owner-only" : ""}
              className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border transition-colors ${
                active ? "bg-zinc-800 text-white border-zinc-800"
                : ownerGated ? "ek-line ek-text-muted cursor-not-allowed"
                : "ek-line ek-text-soft hover:bg-zinc-50"}`}>
              {ownerGated && <Lock size={10} />}
              {s === "verified" ? "Mark Ekselens-verified" : s === "under_review" ? "Under review" : "Illustrative"}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ContentGovernancePanel() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/enterprise360/content-governance").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSet = useCallback(async (sourceId, status, note) => {
    try {
      await api.post(`/admin/enterprise360/content-governance/${sourceId}/status`, { status, note });
      toast.success("Content status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
      throw e;
    }
  }, [load]);

  const onSaveContent = useCallback(async (sourceId, content) => {
    try {
      await api.post(`/admin/enterprise360/content-governance/${sourceId}/content`, { content });
      toast.success("Authoritative content saved — moved to under review");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save content");
      throw e;
    }
  }, [load]);

  if (!data) return <div className="text-sm ek-text-muted py-4">Loading content governance…</div>;
  return (
    <div data-testid="content-governance-panel" className="mt-8">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={16} className="text-[var(--e360-primary)]" />
        <h3 className="text-[15px] font-semibold ek-text">Authoritative content governance</h3>
      </div>
      <p className="text-[12px] ek-text-muted max-w-2xl mb-4">
        Library-level verification for the shared engine content sources. Until a source is
        <b> Ekselens-verified</b>, packs carry an "illustrative — for guidance" caveat and the benchmark
        is watermarked. Verifying is owner-only, so authoritative status stays under your control.
        {!data.can_verify && <span className="text-amber-600"> You can move sources to review; only the owner can verify.</span>}
      </p>
      <div className="space-y-3">
        {data.sources.map((src) => (
          <SourceRow key={src.id} src={src} statuses={data.statuses} canVerify={data.can_verify} onSet={onSet} onSaveContent={onSaveContent} />
        ))}
      </div>
    </div>
  );
}
